import {ResearchLitElement, html, css} from '@core/research-lit-element';

import '@bog-design-system/bd-modal';
import '@bog-design-system/bd-scroll';
import '../../../common/filter/research-filter';
import '../../../common/filter/research-filter-item';

class ResearchCatalogFilterModal extends ResearchLitElement {
    static get is() {
        return 'research-catalog-filter-modal';
    }

    static get styles() {
        return css`
            :host {
                display: flex;
                flex-direction: column;
            }

            .content-wrapper {
                padding: var(--space-48) var(--space-96);
                display: flex;
                flex-direction: column;
                gap: var(--space-24);
            }

            .footer {
                display: flex;
                justify-content: flex-end;
            }

            .left-button {
                margin-right: var(--space-24);
            }

            @media (max-width: 768px) {
                .content-wrapper {
                    padding: var(--space-32) var(--space-64);
                }
            }

            @media (max-width: 600px) {
                .content-wrapper {
                    padding: var(--space-24);
                }

                .footer {
                    padding-left: var(--space-24);
                    padding-right: var(--space-24);
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    grid-column-gap: var(--space-8);
                }

                .left-button {
                    margin-right: var(--space-8);
                }
            }
        `;
    }

    render() {
        return html`
            <bd-modal-header divider>
                <bd-font type="overline1" slot="title"> ${this.data?.dictionary?.modalTitle} </bd-font>
                <bd-button type="secondary" icon="bd:close" icon-only slot="close" dropdownicon="bd:chevron_down">
                </bd-button>
            </bd-modal-header>

            <bd-scroll>
                <div class="content-wrapper">
                    ${this.segments?.length > 0 ?
                        html`
                              <research-filter
                                  active
                                  class="modal-filter"
                                  title="${this.data.dictionary?.segmentsLabel}"
                                  ._selected="${this._filterSegment}"
                                  @filter-changed="${this._handleChangedSegment}"
                              >
                                  ${this.segments.map(
                                      (item) => html`
                                          <research-filter-item
                                              slot="filter-item"
                                              ?checked="${this._filterSegment?.includes(item.tagName)}"
                                              label="${item.title}"
                                              value="${item.tagName}"
                                              items-count="${this._counter.segments[item.tagName] ?? 0}"
                                          >
                                          </research-filter-item>
                                      `
                                  )}
                              </research-filter>
                          ` :
                        ``}
                    ${this.devices?.length > 0 ?
                        html`
                              <research-filter
                                  active
                                  class="modal-filter"
                                  title="${this.data.dictionary?.devicesLabel}"
                                  ._selected="${this._filterDevice}"
                                  @filter-changed="${this._handleChangedDevice}"
                              >
                                  ${this.devices.map(
                                      (item) => html`
                                          <research-filter-item
                                              slot="filter-item"
                                              ?checked="${this._filterDevice?.includes(item.tagName)}"
                                              label="${item.title}"
                                              value="${item.tagName}"
                                              items-count="${this._counter.devices[item.tagName] ?? 0}"
                                          >
                                          </research-filter-item>
                                      `
                                  )}
                              </research-filter>
                          ` :
                        ``}
                    ${this.locations?.length > 0 ?
                        html`
                              <research-filter
                                  active
                                  class="modal-filter"
                                  title="${this.data.dictionary?.locationsLabel}"
                                  ._selected="${this._filterLocation}"
                                  @filter-changed="${this._handleChangedLocation}"
                              >
                                  ${this.locations.map(
                                      (item) => html`
                                          <research-filter-item
                                              slot="filter-item"
                                              ?checked="${this._filterLocation?.includes(item.tagName)}"
                                              label="${item.title}"
                                              value="${item.tagName}"
                                              items-count="${this._counter.locations[item.tagName] ?? 0}"
                                          >
                                          </research-filter-item>
                                      `
                                  )}
                              </research-filter>
                          ` :
                        ``}
                </div>
            </bd-scroll>

            <bd-modal-footer class="footer" divider>
                <bd-button type="primary-uppercase" class="footer-btn left-button" @click="${this._clearFilters}">
                    ${this.data.dictionary.clearBtnTitle}
                </bd-button>

                <bd-button type="primary" class="footer-btn" @click="${this._closeModal}">
                    ${this.data.dictionary.filterBtnTitle}
                </bd-button>
            </bd-modal-footer>
        `;
    }

    static get properties() {
        return {
            data: {
                type: Object,
            },
            segments: {
                type: Array,
            },
            devices: {
                type: Array,
            },
            locations: {
                type: Array,
            },
            _filterSegment: {
                type: Array,
            },
            _filterDevice: {
                type: Array,
            },
            _filterLocation: {
                type: Array,
            },
            _counter: {
                type: Object,
            },
        };
    }

    constructor() {
        super();
        this.data = {};
        this.segments = [];
        this.devices = [];
        this.locations = [];
        this._counter = {
            segments: {},
            devices: {},
            locations: {},
        };
    }

    _closeModal() {
        this._changedModalFilters();
        this.dispatchEvent(
            new CustomEvent('close-info-modal', {
                bubbles: true,
                composed: true,
            })
        );
    }

    _clearFilters() {
        this._filterSegment = [];
        this._filterDevice = [];
        this._filterLocation = [];

        const event = new CustomEvent('clear-filters', {
            bubbles: true,
            composed: true,
        });

        this.dispatchEvent(
            new CustomEvent('close-info-modal', {
                bubbles: true,
                composed: true,
            })
        );

        this.dispatchEvent(event);
    }
    _handleChangedSegment(e) {
        const selected = e.detail?.selected;
        this._filterSegment = selected || null;
        this.sendAnalyticsEvent({
            eventName: 'Research_Bog',
            eventAction: 'segment_filter_click',
            eventLabel: selected?.join(',') || '',
            isNonInteraction: false,
            sendToFB: true,
        });
    }

    _handleChangedDevice(e) {
        const selected = e.detail?.selected;
        this._filterDevice = selected || null;
        this.sendAnalyticsEvent({
            eventName: 'Research_Bog',
            eventAction: 'device_filter_click',
            eventLabel: selected?.join(',') || '',
            isNonInteraction: false,
            sendToFB: true,
        });
    }

    _handleChangedLocation(e) {
        const selected = e.detail?.selected;
        this._filterLocation = selected || null;
        this.sendAnalyticsEvent({
            eventName: 'Research_Bog',
            eventAction: 'location_filter_click',
            eventLabel: selected?.join(',') || '',
            isNonInteraction: false,
            sendToFB: true,
        });
    }

    _changedModalFilters(e) {
        this.dispatchEvent(
            new CustomEvent('changed-filters', {
                detail: {
                    selectedSegment: this._filterSegment,
                    selectedDevice: this._filterDevice,
                    selectedLocation: this._filterLocation,
                },
                bubbles: true,
            })
        );
    }
}

window.customElements.define(ResearchCatalogFilterModal.is, ResearchCatalogFilterModal);
